import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Briefcase, Target, ClipboardCheck, MapPin, CalendarDays, CircleAlert, Radar } from "lucide-react";
import { teamApi, monitoredAreasApi } from "@/api/client";
import { InviteDialog } from "@/components/forms/InviteDialog";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" }) : "—");
const TYPE_META = { field: "F", phone: "P", email_desk: "E" };
const TYPE_CLS = { field: "bg-teal-100 text-teal-800", phone: "bg-indigo-100 text-indigo-800", email_desk: "bg-amber-100 text-amber-800" };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Team() {
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [avail, setAvail] = useState(null);
  const [invite, setInvite] = useState(false);
  const [repFilter, setRepFilter] = useState("");
  const [health, setHealth] = useState(null);

  const load = async () => {
    const [t, a] = await Promise.all([teamApi.get(), teamApi.availability()]);
    setTeam(t.data); setAvail(a.data);
    monitoredAreasApi.health().then((r) => setHealth(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const weekDays = () => {
    if (!avail) return [];
    const start = new Date(avail.from);
    return [...Array(7)].map((_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d.toISOString().slice(0, 10); });
  };
  const availFor = (uid, dstr) => (avail?.items || []).filter((x) => x.user_id === uid && x.business_date === dstr);

  if (!team) return <div className="mx-auto max-w-4xl px-4 py-6"><div className="h-40 animate-pulse rounded-xl border border-slate-200 bg-white" /></div>;
  const reps = repFilter ? team.reps.filter((r) => r.user_id === repFilter) : team.reps;

  return (
    <div className="mx-auto max-w-4xl px-4 py-5 md:py-8" data-testid="team-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Team</h1>
        <button data-testid="team-invite-btn" onClick={() => setInvite(true)} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white hover:bg-teal-800"><UserPlus className="h-4 w-4" /> Invite</button>
      </div>

      {health && health.summary.total > 0 && (
        <button data-testid="team-areas-health" onClick={() => navigate("/monitored-areas")}
          className={`mb-4 flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm ${health.summary.failing > 0 ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`}>
          {health.summary.failing > 0 ? (
            <>
              <CircleAlert className="h-4 w-4 shrink-0 text-rose-600" />
              <span data-testid="team-failing-areas" className="font-semibold text-rose-700">{health.summary.failing} monitored area{health.summary.failing > 1 ? "s" : ""} failing to scan</span>
            </>
          ) : (
            <>
              <Radar className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="font-semibold text-slate-600">{health.summary.total} monitored area{health.summary.total > 1 ? "s" : ""} scanning normally</span>
            </>
          )}
          <span className="ml-auto text-xs font-semibold text-teal-700">View →</span>
        </button>
      )}

      <select data-testid="team-rep-filter" value={repFilter} onChange={(e) => setRepFilter(e.target.value)} className="mb-4 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm">
        <option value="">All reps</option>
        {team.reps.map((r) => <option key={r.user_id} value={r.user_id}>{r.display_name}</option>)}
      </select>

      {/* Rep cards */}
      <div className="space-y-3" data-testid="team-reps">
        {reps.map((r) => (
          <div key={r.user_id} data-testid={`team-rep-${r.user_id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-heading text-sm font-bold text-slate-800">{r.display_name}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold capitalize text-slate-500">{r.role?.replace(/_/g, " ")}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat icon={Briefcase} label="Leads" value={r.assigned_organisations} />
              <Stat icon={Target} label="Opps" value={r.open_opportunities} />
              <Stat icon={ClipboardCheck} label="Tasks" value={r.open_tasks} />
              <Stat icon={MapPin} label="Visits" value={r.upcoming_visits} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              <span><CalendarDays className="mr-1 inline h-3 w-3" />Next field: <b className="text-slate-700">{fmtDate(r.next_field_day?.start_at)}</b></span>
              <span>Phone: <b className="text-slate-700">{fmtDate(r.next_phone_window?.start_at)}</b></span>
              <span>Email/Desk: <b className="text-slate-700">{fmtDate(r.next_email_desk_window?.start_at)}</b></span>
            </div>
          </div>
        ))}
      </div>

      {/* Availability grid */}
      <h2 className="mb-2 mt-6 font-heading text-sm font-bold uppercase tracking-wider text-slate-500">This week's availability</h2>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="team-availability-grid">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-100 text-slate-400">
              <th className="p-2 font-semibold">Rep</th>
              {weekDays().map((d, i) => <th key={d} className="p-2 text-center font-semibold">{DAYS[i]}</th>)}
            </tr>
          </thead>
          <tbody>
            {reps.map((r) => (
              <tr key={r.user_id} className="border-b border-slate-50 last:border-0">
                <td className="p-2 font-semibold text-slate-700">{r.display_name}</td>
                {weekDays().map((d) => (
                  <td key={d} className="p-2 text-center">
                    <div className="flex flex-wrap justify-center gap-0.5">
                      {availFor(r.user_id, d).map((a) => (
                        <span key={a.id} data-testid={`grid-${r.user_id}-${d}`} className={`inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold ${TYPE_CLS[a.availability_type]}`}>{TYPE_META[a.availability_type]}</span>
                      ))}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InviteDialog open={invite} onOpenChange={setInvite} onCreated={load} />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 p-2 text-center">
      <Icon className="mx-auto h-4 w-4 text-slate-400" />
      <p className="mt-0.5 text-base font-bold text-slate-800">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );
}
